import { graphql } from 'gatsby'
import React from 'react'

import { type ButtonProps } from '@/components/button/button'
import { generateUrl } from '@/internal/utils/generate-url'
import { getModelMessages } from '@/utils/get-model-messages'

import Highlight from './highlight/highlight'
import HighlightFullWidth from './highlight-full-width/highlight-full-width'

const FeaturedContent: React.FC<Queries.FeaturedContentFragment> = ({
  display,
  content,
  title
}) => {
  if (content === null) return null
  const {
    __typename: typeName,
    slug,
    image,
    topic,
    externalName,
    excerpt
  } = content
  const modelMessages = getModelMessages(typeName)
  const link = slug ? generateUrl(typeName, slug) : ''

  let persons:
    | (
        | {
            readonly __typename: 'DatoCmsGctPerson'
            readonly firstName: string | null
            readonly secondName: string | null
            readonly job: string | null
          }
        | undefined
        | null
      )[]
    | undefined
    | null
  if (typeName !== 'DatoCmsPctPage') {
    persons = content.authors?.map((item) => item?.person)
  }

  if (display === 'basic') {
    const buttons: ButtonProps[] = [{ to: link, title: modelMessages.button }]
    if (modelMessages.buttonAll) {
      buttons.push({
        to: modelMessages.buttonAllUrl ?? '',
        title: modelMessages.buttonAll
      })
    }

    return (
      <Highlight
        __typename={typeName}
        headline={title}
        topic={topic}
        title={externalName}
        excerpt={excerpt}
        persons={persons}
        buttons={buttons}
        link={link}
        image={image}
      />
    )
  }
  const footer =
    persons && persons?.length > 0
      ? `${modelMessages.label} by ${persons[0]?.firstName} ${persons[0]?.secondName}`
      : undefined

  const imageFull =
    content.__typename !== 'DatoCmsPctPaper' ? content.imageFull : null

  return (
    <HighlightFullWidth
      __typename={typeName}
      image={imageFull}
      title={externalName}
      topic={topic}
      link={link}
      excerpt={excerpt}
      footer={footer}
    />
  )
}

export default FeaturedContent

export const query = graphql`
  fragment FeaturedContent on DatoCmsFeaturedContent {
    __typename
    id
    title
    navigationLabel
    display
    content {
      __typename
      ... on DatoCmsPctPost {
        externalName
        slug
        image: imageCard {
          ...imgHighlightBasic
        }
        imageFull: imageCard {
          ...imgHighlightFullWidth
        }
        topic {
          ...TopicReference
        }
        excerpt
        authors {
          person {
            ...PersonReference
          }
        }
      }
      ... on DatoCmsPctProject {
        externalName
        slug
        image: imageCard {
          ...imgHighlightBasic
        }
        imageFull: imageCard {
          ...imgHighlightFullWidth
        }
        topic {
          ...TopicReference
        }
        excerpt
        authors {
          person {
            ...PersonReference
          }
        }
      }
      ... on DatoCmsPctPage {
        externalName
        slug
        image: imageCard {
          ...imgHighlightBasic
        }
        imageFull: imageCard {
          ...imgHighlightFullWidth
        }
        excerpt
        topic {
          ...TopicReference
        }
      }
      ... on DatoCmsPctPaper {
        externalName
        slug
        image {
          ...imgHighlightBasic
        }
        imageFull: image {
          ...imgHighlightFullWidth
        }
        topic {
          ...TopicReference
        }
        excerpt
        authors {
          person {
            ...PersonReference
          }
        }
      }
      ... on DatoCmsPctDialog {
        externalName
        slug
        image: imageCard {
          ...imgHighlightBasic
        }
        imageFull: imageCard {
          ...imgHighlightFullWidth
        }
        topic {
          ...TopicReference
        }
        excerpt
        authors: speakers {
          person {
            ...PersonReference
          }
        }
      }
      ... on DatoCmsPctEpisode {
        externalName
        slug
        image: imageCard {
          ...imgHighlightBasic
        }
        imageFull: imageCard {
          ...imgHighlightFullWidth
        }
        topic {
          ...TopicReference
        }
        excerpt
        authors {
          person {
            ...PersonReference
          }
        }
        guest {
          person {
            ...PersonReference
          }
        }
      }
      ... on DatoCmsPctExternalContent {
        externalName
        slug: url
        image: imageCard {
          ...imgHighlightBasic
        }
        imageFull: imageCard {
          ...imgHighlightFullWidth
        }
        topic {
          ...TopicReference
        }
        excerpt
        authors {
          person {
            ...PersonReference
          }
        }
      }
    }
  }
`
